// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@pace/App/Components/Common/XngZone";
import { xCommonLoadingOverlay } from "@pace/App/Handlers/XNG/Common";
import { CustomAddAccountApiParameter } from "@pace/App/Services/CreateAccountService";
import { parseBoolean, xValidateOnSubmit } from "@pace/XDL/Utilities";
import React, { RefObject, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    buttonLink = "buttonLink",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonLink]: LinkFieldValue;
    };
}

const mapDispatchToProps = {
    addAccount,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    xCommonLoadingOverlay,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomCreateAccountStyles {
    form?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    existingCustomerGridItem?: GridItemProps;
    customSiteMessage?: TypographyProps;
    centeredGridItem?: GridItemProps;
    applicationsButton?: ButtonPresentationProps;
    firstNameGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    phoneNumberGridItem?: GridItemProps;
    positionGridItem?: GridItemProps;
    accountNumberRadioGridItem?: GridItemProps;
    accountNumberGridItem?: GridItemProps;
    existingCustomerRadioGroup?: RadioGroupProps;
    accountNumberRadioGroup?: RadioGroupProps;
    textFieldStyle?: TextFieldPresentationProps;
    companyNameGridItem?: GridItemProps;
    companyAddressGridItem?: GridItemProps;
}

export const customCreateAccountStyles: CustomCreateAccountStyles = {};

const styles = customCreateAccountStyles;
const StyledForm = getStyledWrapper("form");

const CustomCreateAccount = ({ addAccount, validateReCaptcha, id, fields, xCommonLoadingOverlay }: Props) => {
    const [email, setEmail] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [position, setPosition] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [accountCreated, setAccountCreated] = useState(false);
    const [isExistingCustomer, setIsExistingCustomer] = useState("");
    const [haveCustomerNumber, setHaveCustomerNumber] = useState("false");
    const [companyName, setCompanyName] = useState("");
    const [address, setAddress] = useState("");
    const { isInShell } = useShellContext();
    const history = useHistory();
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const { url, title } = useGetLink(fields.buttonLink);

    const openLink = () => {
        if (!url || isInShell) {
            return;
        }
        if (url.startsWith("http")) {
            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const firstNameChangeHandler = (firstName: string) => {
        setFirstName(firstName);
    };
    const lastNameChangeHandler = (lastName: string) => {
        setLastName(lastName);
    };
    const phoneNumberChangeHandler = (phoneNumber: string) => {
        setPhoneNumber(phoneNumber);
    };
    const positionChangeHandler = (positionTitle: string) => {
        setPosition(positionTitle);
    };
    const accountNumberChangeHandler = (accountNumber: string) => {
        setAccountNumber(accountNumber);
    };
    const companyNameChangeHandler = (companyName: string) => {
        setCompanyName(companyName);
    };
    const addressChangeHandler = (address: string) => {
        setAddress(address);
    };

    const emailChangeHandler = (email: string) => {
        setEmail(email);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        xCommonLoadingOverlay({ key: "clear", value: "Create_Account" });
        setAccountCreated(true);
    };
    
    // Create multiple formIds for validation
    // as we have multiple fields could hide/show
    // based on haveCustomerNumber value

    const formId = "createAccountForm";
    const accountNumberFormId = "createAccountForm-AccountNumber";
    const companyAddressFormId = "createAccountForm-CompanyFields";

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        const errors = xValidateOnSubmit(formId);
        const accountNumberErrors = parseBoolean(haveCustomerNumber) ? xValidateOnSubmit(accountNumberFormId) : [];
        const companyAddressErrors = !parseBoolean(haveCustomerNumber) ? xValidateOnSubmit(companyAddressFormId) : [];

        if (errors.length > 0 || accountNumberErrors.length > 0 || companyAddressErrors?.length >  0) {
            return;
        }

        xCommonLoadingOverlay({ key: "add", value: "Create_Account" });
        addAccount({
            firstName,
            lastName,
            phoneNumber,
            email,
            userTitle: position,
            customerNumber: accountNumber,
            companyName,
            address,
            onSuccess: onSuccessHandler,
            onError: (error => {
                setErrorMessage(error);
                xCommonLoadingOverlay({ key: "clear", value: "Create_Account" });
            }) as CustomAddAccountApiParameter["onError"],
        } as any);
    };

    if (accountCreated) {
        return <XngZone hint="AccountCreation Confirmation Zone" zoneName="AccountCreated" contentId={id} />;
    }

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.mainGridContainer}>
                <GridItem {...styles.existingCustomerGridItem}>
                    <RadioGroup
                        name="isPaceExistingCustomer"
                        label={translate("Are you an existing Pace Supply Customer")}
                        value={isExistingCustomer}
                        onChangeHandler={e => {
                            setIsExistingCustomer(e.currentTarget.value);
                        }}
                        horizontal={false}
                        {...styles.existingCustomerRadioGroup}
                    >
                        <Radio value="true">{translate("Yes")}</Radio>
                        <Radio value="false">{translate("No")}</Radio>
                    </RadioGroup>
                </GridItem>
                {isExistingCustomer === "false" && (
                    <GridItem {...styles.centeredGridItem}>
                        <GridContainer>
                            <GridItem {...styles.centeredGridItem}>
                                <Typography {...styles.customSiteMessage}>
                                    {siteMessage("Custom_CreateAccount_Licensed_Customer")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.centeredGridItem}>
                                <Button onClick={() => openLink()} {...styles.applicationsButton}>
                                    {translate("Applications")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                )}

                {isExistingCustomer === "true" && (
                    <GridItem {...styles.userGridItem}>
                        <GridContainer {...styles.userGridContainer}>
                            <GridItem {...styles.firstNameGridItem}>
                                <TextField
                                    required={true}
                                    type="firstName"
                                    label={translate("First Name")}
                                    value={firstName}
                                    onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                                    {...{ formId }}
                                    {...styles.textFieldStyle}
                                />
                            </GridItem>
                            <GridItem {...styles.lastNameGridItem}>
                                <TextField
                                    required={true}
                                    type="lastName"
                                    label={translate("Last Name")}
                                    value={lastName}
                                    onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                                    {...{ formId }}
                                    {...styles.textFieldStyle}
                                />
                            </GridItem>
                            <GridItem {...styles.emailGridItem}>
                                <TextField
                                    {...styles.emailTextField}
                                    type="email"
                                    label={translate("Email Address")}
                                    onChange={e => emailChangeHandler(e.currentTarget.value)}
                                    value={email}
                                    data-test-selector="createAccount_email"
                                    required={true}
                                    {...{ formId }}
                                    {...styles.textFieldStyle}
                                />
                            </GridItem>
                            <GridItem {...styles.phoneNumberGridItem}>
                                <TextField
                                    required={true}
                                    label={translate("Contact Phone Number")}
                                    placeholder="( ___ ) ___ - ____"
                                    value={phoneNumber}
                                    onChange={e => phoneNumberChangeHandler(e.currentTarget.value)}
                                    type="phone"
                                    {...{ formId }}
                                    {...styles.textFieldStyle}
                                />
                            </GridItem>
                            <GridItem {...styles.positionGridItem}>
                                <TextField
                                    label={translate("Position")}
                                    type="text"
                                    onChange={e => positionChangeHandler(e.currentTarget.value)}
                                    value={position}
                                    data-test-selector="createAccount_position"
                                    required={false}
                                    {...{ formId }}
                                    {...styles.textFieldStyle}
                                />
                            </GridItem>
                            <GridItem {...styles.accountNumberRadioGridItem}>
                                <RadioGroup
                                    name="haveCustomerNumber"
                                    label={translate("Do you know your Account Number?")}
                                    value={haveCustomerNumber}
                                    onChangeHandler={e => {
                                        setHaveCustomerNumber(e.currentTarget.value);
                                    }}
                                    horizontal={false}
                                    {...styles.accountNumberRadioGroup}
                                >
                                    <Radio value="true">{translate("Yes")}</Radio>
                                    <Radio value="false">{translate("No")}</Radio>
                                </RadioGroup>
                            </GridItem>
                            {parseBoolean(haveCustomerNumber) ? (
                                <GridItem {...styles.accountNumberGridItem}>
                                    <TextField
                                        label={translate("Account #")}
                                        type="text"
                                        onChange={e => accountNumberChangeHandler(e.currentTarget.value)}
                                        value={accountNumber}
                                        data-test-selector="createAccount_accountNumber"
                                        required={true}
                                        {...{ formId: accountNumberFormId }}
                                        {...styles.textFieldStyle}
                                    />
                                </GridItem>
                            ) : (
                                <>
                                    <GridItem {...styles.companyNameGridItem}>
                                        <TextField
                                            label={translate("Company Name")}
                                            type="text"
                                            onChange={e => companyNameChangeHandler(e.currentTarget.value)}
                                            value={companyName}
                                            data-test-selector="createAccount_companyName"
                                            required={!parseBoolean(haveCustomerNumber)}
                                            {...{ formId: companyAddressFormId }}
                                            {...styles.textFieldStyle}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.companyAddressGridItem}>
                                        <TextField
                                            label={translate("Company Address")}
                                            type="text"
                                            onChange={e => addressChangeHandler(e.currentTarget.value)}
                                            value={address}
                                            data-test-selector="createAccount_address"
                                            required={!parseBoolean(haveCustomerNumber)}
                                            {...{ formId: companyAddressFormId }}
                                            {...styles.textFieldStyle}
                                        />
                                    </GridItem>
                                </>
                            )}
                            {errorMessage && (
                                <GridItem
                                    data-test-selector="createAccount_ErrorMessage"
                                    {...styles.submitErrorGridItem}
                                >
                                    <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                                </GridItem>
                            )}
                            <GridItem {...styles.reCaptchaGridItem}>
                            <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                            </GridItem>
                            <GridItem {...styles.submitGridItem}>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    data-test-selector="createAccount_createButton"
                                >
                                    {translate("Submit")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                )}
                <GridItem {...styles.centeredGridItem}>
                    <Typography {...styles.customSiteMessage}>
                        {siteMessage("Custom_CreateAccount_Pace_Customer")}
                    </Typography>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CustomCreateAccount),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
        fieldDefinitions: [
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
                allowCategories: (item: HasFields) => false,
                allowUrls: (item: HasFields) => true,
            },
        ],
    },
};

export default widgetModule;
